import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight } from 'lucide-react';
import api from '../services/api';

interface SliderHumanoProps {
  // Recebe a verificação assinada pelo servidor (ou null quando expira/é reiniciada)
  onChange: (verificacao: string | null) => void;
  // Mude este valor para reiniciar o slider (ex.: após erro de verificação no login)
  resetKey?: number;
  // Avisa se o servidor exige o desafio (LOGIN_DESAFIO_HUMANO=false desliga)
  onHabilitado?: (habilitado: boolean) => void;
}

const LARGURA_BOTAO = 44;
const VALIDADE_VERIFICACAO_MS = 110000; // servidor valida por 2 min

const SliderHumano: React.FC<SliderHumanoProps> = ({ onChange, resetKey = 0, onHabilitado }) => {
  const { t } = useTranslation();
  const trilhoRef = useRef<HTMLDivElement>(null);
  const desafioRef = useRef<string | null>(null);
  const arrasto = useRef<{ inicio: number; pontos: number; origemX: number } | null>(null);
  const [posicao, setPosicao] = useState(0);
  const [estado, setEstado] = useState<'carregando' | 'pronto' | 'verificando' | 'ok' | 'erro'>('carregando');

  const carregarDesafio = useCallback(async () => {
    setPosicao(0);
    setEstado('carregando');
    onChange(null);
    try {
      const { data } = await api.get('/usuarios/desafio-humano');
      onHabilitado?.(data.habilitado !== false);
      desafioRef.current = data.desafio;
      setEstado('pronto');
    } catch {
      setEstado('erro');
    }
  }, [onChange, onHabilitado]);

  useEffect(() => {
    carregarDesafio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Verificação expira no servidor: reinicia o slider antes disso
  useEffect(() => {
    if (estado !== 'ok') return;
    const timer = setTimeout(carregarDesafio, VALIDADE_VERIFICACAO_MS);
    return () => clearTimeout(timer);
  }, [estado, carregarDesafio]);

  const larguraMaxima = () => Math.max((trilhoRef.current?.clientWidth || 0) - LARGURA_BOTAO, 1);

  const concluir = async (duracaoMs: number, pontos: number) => {
    setEstado('verificando');
    try {
      const { data } = await api.post('/usuarios/desafio-humano', { desafio: desafioRef.current, duracaoMs, pontos });
      setPosicao(larguraMaxima());
      setEstado('ok');
      onChange(data.verificacao);
    } catch {
      carregarDesafio();
    }
  };

  const iniciar = (e: React.PointerEvent<HTMLDivElement>) => {
    if (estado !== 'pronto') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    arrasto.current = { inicio: Date.now(), pontos: 0, origemX: e.clientX - posicao };
  };

  const mover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!arrasto.current) return;
    arrasto.current.pontos += 1;
    setPosicao(Math.min(Math.max(e.clientX - arrasto.current.origemX, 0), larguraMaxima()));
  };

  const soltar = () => {
    const atual = arrasto.current;
    arrasto.current = null;
    if (!atual) return;
    if (posicao >= larguraMaxima() - 2) {
      concluir(Date.now() - atual.inicio, atual.pontos);
    } else {
      setPosicao(0);
    }
  };

  const ok = estado === 'ok';
  const rotulo = ok ? t('login.humanVerified') : estado === 'erro' ? t('login.humanError') : t('login.humanSlide');

  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 dark:text-gray-400">{t('login.humanInstruction')}</p>
      <div
        ref={trilhoRef}
        className={`relative h-11 rounded-md overflow-hidden select-none border ${ok ? 'bg-green-100 border-green-400 dark:bg-green-900/40' : 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600'}`}
        style={{ touchAction: 'none' }}
      >
        <div className="absolute inset-y-0 left-0 bg-green-300/60 dark:bg-green-700/40" style={{ width: posicao + LARGURA_BOTAO }} />
        <span className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 dark:text-gray-300 pointer-events-none">
          {rotulo}
        </span>
        <div
          role="slider"
          aria-label={t('login.humanInstruction')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round((posicao / larguraMaxima()) * 100)}
          className={`absolute top-0 bottom-0 flex items-center justify-center rounded-md bg-white dark:bg-gray-200 text-gray-800 shadow ${ok ? '' : 'cursor-grab active:cursor-grabbing'}`}
          style={{ width: LARGURA_BOTAO, left: posicao, transition: arrasto.current ? 'none' : 'left 0.2s' }}
          onPointerDown={iniciar}
          onPointerMove={mover}
          onPointerUp={soltar}
          onPointerCancel={soltar}
        >
          {ok ? <Check className="h-4 w-4 text-green-600" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </div>
    </div>
  );
};

export default SliderHumano;
